// Four-section editor for the RA "activity" header field. Used by the Builder
// (create flow) and the Detail page (draft edit). Backed by a single text
// column on the server — round-trips through parseActivity/serializeActivity
// in client/src/utils/activityFormat.js. Legacy plain-text activities surface
// in the Description sub-field automatically.
import React, { useState, useEffect } from 'react';
import { parseActivity, serializeActivity } from '../utils/activityFormat';

export default function ActivityField({ value, canEdit, onSave, onDirty }) {
  const [fields, setFields] = useState(() => parseActivity(value));
  // Resync when the server pushes a new payload (e.g. after another save).
  useEffect(() => { setFields(parseActivity(value)); }, [value]);

  const commit = (next) => {
    const serialized = serializeActivity(next);
    if (serialized !== (value || '')) onSave(serialized);
  };

  const inputClass = 'block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-70';
  const labelClass = 'block text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1';
  const update = (key, v) => {
    setFields((prev) => {
      const next = { ...prev, [key]: v };
      // Signal dirty as soon as serialized diverges from `value`, so the
      // save-bar pill flips on keystroke instead of waiting for blur.
      if (onDirty && serializeActivity(next) !== (value || '')) onDirty();
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <label className="block">
        <span className={labelClass}>Activity name</span>
        <input
          type="text"
          value={fields.name}
          onChange={(e) => update('name', e.target.value)}
          onBlur={() => commit(fields)}
          disabled={!canEdit}
          placeholder="e.g. Wind on A-Stage — Episode 4"
          className={inputClass}
        />
      </label>
      <label className="block">
        <span className={labelClass}>Activity description</span>
        <textarea
          rows={4}
          value={fields.description}
          onChange={(e) => update('description', e.target.value)}
          onBlur={() => commit(fields)}
          disabled={!canEdit}
          placeholder="e.g. Generating wind across the A-Stage interior set for the storm sequence. Two 5ft electric wind machines operated by crew from a safe perimeter, running for 2-3 minute takes throughout the shoot day."
          className={`${inputClass} resize-y`}
        />
      </label>
      <label className="block">
        <span className={labelClass}>Equipment overview</span>
        <textarea
          rows={4}
          value={fields.equipment}
          onChange={(e) => update('equipment', e.target.value)}
          onBlur={() => commit(fields)}
          disabled={!canEdit}
          placeholder="e.g. 2 × 5ft electric wind machines, 32A 3-phase distro, anchor points to studio floor pockets, comms headsets, fire extinguishers within 5m."
          className={`${inputClass} resize-y`}
        />
      </label>
      <label className="block">
        <span className={labelClass}>Methodology</span>
        <textarea
          rows={4}
          value={fields.methodology}
          onChange={(e) => update('methodology', e.target.value)}
          onBlur={() => commit(fields)}
          disabled={!canEdit}
          placeholder="e.g. 1) Set up machines and anchor to floor before crew arrive. 2) Brief talent and crew on signal/cut procedure. 3) Ramp speed up gradually to take level. 4) Operator stays at controls for full run; assistant watches blades. 5) Power down before anyone approaches set."
          className={`${inputClass} resize-y`}
        />
      </label>
    </div>
  );
}
